import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { LangProvider } from './i18n';
import { LangSettingsSync } from './lib/user-settings';
import { ThemeProvider } from './theme';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthGate } from './components/AuthGate';
import { ErrorOverlay } from './components/ErrorOverlay';
import { UpdateReadyBanner } from './components/UpdateReadyBanner';
import { ConnectionBanner } from './components/ConnectionBanner';
import { ClientOutdatedDialog } from './components/ClientOutdatedDialog';
import { installGlobalErrorHandlers } from './lib/errors';
import { installConnectivity } from './lib/connectivity';
import { SharePointDemo } from './components/SharePointDemo';
import { UserhubShell } from './userhub/UserhubShell';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

installGlobalErrorHandlers();
// Background reads that cannot reach the backend show one "Reconnecting…" banner and pause the
// polls, instead of an error dialog each (lib/connectivity.ts).
installConnectivity();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

// The Graph sign-in (lib/graphAuth.ts) uses a full-page REDIRECT whose reply URL is the app origin,
// so Entra returns to the app ROOT with a `#code=…` fragment carrying no `?sp=1` of its own. The
// `niobe.graphConnect` flag (set just before the redirect) is what tells us this root load is that
// return and not a normal app visit — its PRESENCE, now that the ?sp=1 demo is the only surface
// that signs in to Graph at all.
//
// This test is not cosmetic. Let the ordinary app boot on a Graph return and AuthGate runs FIRST:
// lib/auth.ts shares graphAuth's client id, so it would happily redeem OUR Graph auth code and hand
// the resulting Graph-audience token back as the Niobe API bearer, 401ing the backend. Exactly ONE
// of the two MSAL clients may be constructed on any load.
const params = new URLSearchParams(window.location.search);
const hasAuthResponse = /(?:^|[#&?])(code|error|state)=/.test(window.location.hash + window.location.search);
const isGraphReturn = hasAuthResponse && sessionStorage.getItem('niobe.graphConnect') !== null;

if (params.get('sp') === '1' || isGraphReturn) {
  // ?sp=1 → the standalone SharePoint MCP demo, independent of the Niobe backend + primary auth.
  // It also owns the redirect return: it completes the sign-in and repairs its own address bar.
  createRoot(rootEl).render(
    <StrictMode>
      <SharePointDemo />
    </StrictMode>,
  );
} else {
  createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LangProvider>
        <ThemeProvider>
          <ErrorBoundary>
            <AuthGate>
              {/* The saved UI language follows the member across web and Desktop (AB#388). */}
              <LangSettingsSync />
              {/* userhub feedback context (the account menu's Feedback group) — context only. */}
              <UserhubShell>
                <App />
              </UserhubShell>
            </AuthGate>
          </ErrorBoundary>
          <ErrorOverlay />
          <UpdateReadyBanner />
          <ConnectionBanner />
          <ClientOutdatedDialog />
        </ThemeProvider>
      </LangProvider>
    </QueryClientProvider>
  </StrictMode>,
  );
}
